/**
 * Trava do ponteiro (F99): quando a tecla que fecha um painel pode travar o mouse de novo no próprio keydown
 * (o gesto que o navegador aceita; o quadro seguinte, no Safari e no Firefox, já não é). Puro, para os
 * testes em node.
 */

/** Os painéis abertos, como a cena os vê no keydown (antes de o quadro tratar a tecla). */
export interface OpenPanels {
  inventory: boolean;
  crafting: boolean;
  map: boolean;
  abilities: boolean;
  sheet: boolean;
  settings: boolean;
  chat: boolean;
  trade: boolean;
  raft: boolean;
  /** A Carranca de um aliado (a do solo é a decisão da volta: não fecha por tecla). */
  carranca: boolean;
  carrancaSolo: boolean;
  pause: boolean;
}

/** O que mais decide a tecla além dos painéis (revisão da F99). */
export interface RelockContext {
  /** Uma porta ao alcance na mira: o E é dela, não do painel. */
  doorInReach?: boolean;
  /** As Opções esperam uma tecla para remapear: a tecla é delas, nada fecha. */
  capturingKey?: boolean;
}

/** As ações que fecham painéis; `chat-send` é o Enter dentro do campo do chat. */
export type RelockKey = "inventory" | "crafting" | "map" | "abilities" | "sheet" | "settings" | "interact" | "chat-send";

/** Os painéis que a ação fecha (vazio se ela não fecha nenhum: abre, ou não é com ela). */
function closedBy(key: RelockKey, open: OpenPanels, ctx: RelockContext): (keyof OpenPanels)[] {
  if (ctx.capturingKey) return [];
  switch (key) {
    // Abrir ou fechar a mochila ou a fabricação também fecha o Mercador.
    case "inventory":
    case "crafting":
      return open[key] ? [key, "trade"] : [];
    case "map":
    case "abilities":
    case "sheet":
    case "settings":
      return open[key] ? [key] : [];
    // E fecha o Mercador, a Carranca de um aliado ou o Barco, nessa ordem (como na cena); a porta na mira vem antes.
    case "interact":
      if (ctx.doorInReach) return [];
      return open.trade ? ["trade"] : open.carranca ? ["carranca"] : open.raft ? ["raft"] : [];
    case "chat-send":
      return open.chat ? ["chat"] : [];
  }
}

/**
 * A tecla fecha o último painel aberto: trava de novo agora, no gesto. Com outro painel (ou a pausa) ainda
 * aberto depois dela, o mouse continua dele.
 */
export function relockOnKey(key: RelockKey, open: OpenPanels, ctx: RelockContext = {}): boolean {
  const closed = closedBy(key, open, ctx);
  if (closed.length === 0) return false;
  return (Object.keys(open) as (keyof OpenPanels)[]).every((k) => !open[k] || closed.includes(k));
}

/**
 * Aviso "Clique para voltar ao jogo": o ponteiro já travou nesta partida, está solto e nada aberto o usa.
 * Cobre a trava que o navegador recusou (cooldown do Esc, Safari) e o painel fechado pelo ✕. Sem ter travado nenhuma vez (o headless dos e2e), não aparece.
 */
export function showResumeHint(s: { everLocked: boolean; locked: boolean; panelOpen: boolean }): boolean {
  return s.everLocked && !s.locked && !s.panelOpen;
}
